import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ChevronRight } from "lucide-react";
import { relativeTime, ageFromIso, STATE_STYLES } from "@/lib/format";
import { Sparkline } from "@/components/Sparkline";

function fallbackStateFromAge(latest) {
  if (!latest) return "no_data";
  const age = ageFromIso(latest.recorded_at);
  if (age == null) return "no_data";
  return age > 300 ? "stale" : "ok";
}

export function DashboardSensorTile({ sensor }) {
  const [showRaw, setShowRaw] = useState(false);
  const [, force] = useState(0);
  const navigate = useNavigate();
  useEffect(() => {
    const t = setInterval(() => force((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);

  // Backend computes state (ok|warn|critical|stale|no_data); fall back to
  // local freshness check if not provided (defensive).
  const state = sensor.state || fallbackStateFromAge(sensor.latest);
  const s = STATE_STYLES[state] || STATE_STYLES.no_data;
  const ageLabel = sensor.latest
    ? relativeTime(sensor.latest.recorded_at)
    : "no data yet";

  return (
    <div
      data-testid={`sensor-tile-${sensor.id}`}
      onClick={(e) => {
        // Ignore clicks on the raw toggle or its panel
        if (e.target.closest("[data-noclick]")) return;
        navigate(`/history?sensor=${sensor.id}`);
      }}
      className={`border ${s.border} bg-zinc-900/40 p-6 transition-colors duration-150 cursor-pointer hover:bg-zinc-900/70`}
    >
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">
            {sensor.kind} · {sensor.ip}
          </div>
          <div className="font-display text-2xl font-bold uppercase tracking-tight">
            {sensor.name}
          </div>
        </div>
        <span
          className={`text-[10px] uppercase tracking-widest border px-2 py-1 ${s.pill}`}
          data-testid={`sensor-state-${sensor.id}`}
        >
          {s.label}
        </span>
      </div>

      <div className="flex items-end justify-between gap-3 mb-4">
        <div className="flex items-baseline gap-3">
          <span
            className={`font-display font-black tracking-tighter leading-none text-7xl ${s.text}`}
            data-testid={`sensor-temp-${sensor.id}`}
          >
            {sensor.latest ? sensor.latest.temp_f.toFixed(1) : "—"}
          </span>
          <span className="text-2xl text-zinc-500 font-display font-bold">°F</span>
        </div>
        <div
          className={`flex items-end pb-2 ${s.text}`}
          data-noclick
          onClick={(e) => e.stopPropagation()}
        >
          <Sparkline sensorId={sensor.id} width={120} height={36} />
        </div>
      </div>

      <div className="text-xs text-zinc-400 flex items-center justify-between border-t border-zinc-800 pt-3">
        <span
          title={sensor.latest?.recorded_at || ""}
          data-testid={`sensor-age-${sensor.id}`}
        >
          {ageLabel}
        </span>
        {sensor.latest && (
          <span className="text-zinc-500">
            {Number(sensor.latest.temp_c).toFixed(1)} °C
          </span>
        )}
      </div>

      <button
        type="button"
        data-noclick
        onClick={(e) => {
          e.stopPropagation();
          setShowRaw((v) => !v);
        }}
        data-testid={`sensor-raw-toggle-${sensor.id}`}
        className="mt-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-zinc-500 hover:text-zinc-200"
      >
        {showRaw ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        View raw
      </button>
      {showRaw && (
        <pre
          data-noclick
          onClick={(e) => e.stopPropagation()}
          data-testid={`sensor-raw-${sensor.id}`}
          className="mt-2 max-h-48 overflow-auto bg-zinc-950 border border-zinc-800 text-[11px] text-zinc-300 p-3 font-mono"
        >
          {sensor.latest
            ? JSON.stringify(sensor.latest, null, 2)
            : "no readings yet"}
        </pre>
      )}
    </div>
  );
}
